import { Eye, Archive, Trash2, X, Send, Loader2, StickyNote } from "lucide-react";
import { StatusBadge, TierBadge, PaidBadge } from "./badges";
import { ReplySection } from "./ReplySection";
import { PaymentSection } from "./PaymentSection";

const hasValue = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));

const DetailRow = ({ label, value, full = false }) =>
  hasValue(value) ? (
    <div className={full ? "sm:col-span-2" : undefined}>
      <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-200 whitespace-pre-wrap">{Array.isArray(value) ? value.join(", ") : value}</p>
    </div>
  ) : null;

const NotesSection = ({ notes, noteText, onNoteTextChange, savingNote, onAddNote, onDeleteNote }) => (
  <div data-testid="notes-section" className="mt-8 border-t border-white/10 pt-6">
    <h3 className="font-heading text-lg font-medium flex items-center gap-2">
      <StickyNote className="w-4 h-4 text-violet-400" /> Follow-up notes
      <span className="text-sm text-slate-500 font-normal">({notes.length})</span>
    </h3>
    <div className="mt-4 space-y-3 max-h-60 overflow-y-auto pr-1">
      {notes.length === 0 && (
        <p data-testid="no-notes" className="text-sm text-slate-500">No notes yet. Track calls, emails, and next steps here — visible only to admins.</p>
      )}
      {notes.map((n) => (
        <div key={n.id} data-testid={`note-${n.id}`} className="bg-[#05071F] border border-white/10 p-4 flex gap-3">
          <div className="flex-1">
            <p className="text-sm text-slate-200 whitespace-pre-wrap">{n.text}</p>
            <p className="mt-2 text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}</p>
          </div>
          <button data-testid={`delete-note-${n.id}`} onClick={() => onDeleteNote(n.id)}
            className="text-slate-600 hover:text-red-300 transition-colors self-start"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
    <div className="mt-4 flex gap-2">
      <textarea data-testid="note-input" value={noteText} onChange={(e) => onNoteTextChange(e.target.value)} rows={2}
        placeholder="Add a private note — e.g. 'Called 6/20, wants SD-WAN quote by Friday'…"
        className="flex-1 bg-[#05071F] border border-white/15 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400 resize-none" />
      <button data-testid="add-note-btn" onClick={onAddNote} disabled={savingNote || !noteText.trim()}
        className="self-end inline-flex items-center gap-2 text-sm bg-violet-500 text-white font-semibold px-5 py-2.5 rounded-full hover:bg-violet-400 transition-colors disabled:opacity-50">
        {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Add Note
      </button>
    </div>
  </div>
);

export const InquiryDetailModal = ({ inquiry, onClose, onUpdateStatus, onDelete, onSendReply, onPaymentsChange, onNoteAdded, noteText, onNoteTextChange, savingNote, onAddNote, onDeleteNote }) => (
  <div data-testid="inquiry-detail-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose}>
    <div className="bg-[#0B1035] border border-white/10 w-full max-w-2xl p-8 my-8" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium">{inquiry.org_name}</h2>
          <div className="mt-2 flex items-center gap-2">
            <StatusBadge status={inquiry.status || "new"} />
            <TierBadge tier={inquiry.lead_tier} score={inquiry.lead_score} />
            <PaidBadge count={inquiry.paid_count} total={inquiry.paid_total} />
          </div>
        </div>
        <button data-testid="close-detail-btn" onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <DetailRow label="Industry" value={inquiry.industry} />
        <DetailRow label="Org Type" value={inquiry.org_type} />
        <DetailRow label="HQ" value={inquiry.hq_city_state} />
        <DetailRow label="Employees" value={inquiry.num_employees} />
        <DetailRow label="Website" value={inquiry.website} />
        <DetailRow label="IT Budget" value={inquiry.it_budget} />
        <DetailRow label="Contact" value={inquiry.full_name} />
        <DetailRow label="Title" value={inquiry.title_role} />
        <DetailRow label="Email" value={inquiry.email} />
        <DetailRow label="Phone" value={inquiry.phone} />
        <DetailRow label="Preferred Contact" value={inquiry.preferred_contact} />
        <DetailRow label="Decision Timeline" value={inquiry.decision_timeline} />
        <DetailRow label="Target Start" value={inquiry.target_start} />
        <DetailRow label="Budget Range" value={inquiry.budget_range} />
        <DetailRow label="Services of Interest" value={inquiry.services} full />
        <DetailRow label="Submitted Via" value={inquiry.inquiry_type === "connectivity" ? "Quick connectivity form" : null} full />
        <DetailRow label="Service Address" value={inquiry.service_address} full />
        <DetailRow label="Number of Sites" value={inquiry.num_sites} />
        <DetailRow label="Desired Speed" value={inquiry.desired_speed} />
        <DetailRow label="TV Requirements" value={inquiry.tv_requirements} full />
        <DetailRow label="Current Provider" value={inquiry.current_provider} full />
        <DetailRow label="Environment" value={inquiry.environment_na ? "Marked N/A by prospect" : null} full />
        <DetailRow label="Cloud Providers" value={inquiry.cloud_providers} full />
        <DetailRow label="Security Stack" value={inquiry.security_stack} full />
        <DetailRow label="RMM / Management Tools" value={inquiry.rmm_tools} full />
        <DetailRow label="Network Summary" value={inquiry.network_summary} full />
        <DetailRow label="Locations / Sites" value={inquiry.num_locations} />
        <DetailRow label="Endpoints (approx.)" value={inquiry.num_endpoints} />
        <DetailRow label="Compliance" value={inquiry.compliance} full />
        <DetailRow label="Business Drivers" value={inquiry.business_drivers} full />
        <DetailRow label="Desired Outcomes" value={inquiry.desired_outcomes} full />
        <DetailRow label="Notes" value={inquiry.additional_notes} full />
        <DetailRow label="Printed Name" value={inquiry.printed_name} />
        <DetailRow label="Received" value={new Date(inquiry.created_at).toLocaleString()} />
      </div>
      <ReplySection prospectEmail={inquiry.email} prospectName={inquiry.full_name} onSendReply={onSendReply} />
      <PaymentSection inquiryId={inquiry.id} prospectEmail={inquiry.email} onPaymentsChange={onPaymentsChange} onNoteAdded={onNoteAdded} />
      <NotesSection
        notes={inquiry.notes || []}
        noteText={noteText}
        onNoteTextChange={onNoteTextChange}
        savingNote={savingNote}
        onAddNote={onAddNote}
        onDeleteNote={onDeleteNote}
      />
      <div className="mt-8 flex flex-wrap gap-3 border-t border-white/10 pt-6">
        <button data-testid="mark-reviewed-btn" onClick={() => onUpdateStatus(inquiry.id, "reviewed")}
          className="inline-flex items-center gap-2 text-sm bg-emerald-400/15 text-emerald-300 border border-emerald-400/30 px-4 py-2 rounded-full hover:bg-emerald-400/25 transition-colors">
          <Eye className="w-4 h-4" /> Mark Reviewed
        </button>
        <button data-testid="archive-btn" onClick={() => onUpdateStatus(inquiry.id, "archived")}
          className="inline-flex items-center gap-2 text-sm border border-white/20 text-slate-300 px-4 py-2 rounded-full hover:border-white/50 transition-colors">
          <Archive className="w-4 h-4" /> Archive
        </button>
        <button data-testid="delete-inquiry-btn" onClick={() => onDelete(inquiry.id)}
          className="inline-flex items-center gap-2 text-sm bg-red-400/15 text-red-300 border border-red-400/30 px-4 py-2 rounded-full hover:bg-red-400/25 transition-colors ml-auto">
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      </div>
    </div>
  </div>
);
